import { ref } from 'vue'
import axiosInstance from '@/axios'

/**
 * usePreviewFallbackTools -- shared read of the SuperAdmin
 * ``preview_fallback_tools_enabled`` platform-settings switch.
 *
 * The switch governs the Zone diagram and Open canvas views on the product
 * editor's Stage. Uploading your own image for a color is NOT governed by it
 * (always available).
 *
 * Fail-SAFE default FALSE (Jake, 2026-10-28): ``toolsEnabled`` reads FALSE
 * while the fetch is in flight, if it errors, and if the payload is not a
 * real boolean. The backend's unset default is FALSE too. A failed read must
 * never reveal tools an SA has not enabled -- the opposite direction of
 * useHideUnpreviewableColors, which fails TRUE.
 *
 * One GET per page load: module-level cache + in-flight dedupe.
 *
 *   const { toolsEnabled, loading, error } = usePreviewFallbackTools()
 */
const ENDPOINT = '/api/platform-settings/preview-fallback-tools-enabled'

const _value = ref(false)
const _loading = ref(false)
const _error = ref(null)
let _inflight = null
let _resolved = false

function _fetchOnce() {
    if (_resolved) return Promise.resolve(_value.value)
    if (_inflight) return _inflight
    _loading.value = true
    _error.value = null
    _inflight = axiosInstance
        .get(ENDPOINT)
        .then((resp) => {
            const raw = resp && resp.data ? resp.data.value : null
            // Only a real boolean overrides the fail-safe FALSE.
            _value.value = typeof raw === 'boolean' ? raw : false
            _resolved = true
            return _value.value
        })
        .catch((err) => {
            // Fail SAFE; not marked resolved so a later mount retries.
            _error.value = err
            _value.value = false
            return _value.value
        })
        .finally(() => {
            _loading.value = false
            _inflight = null
        })
    return _inflight
}

/** Test-only: reset the module-level shared state. */
export function _resetPreviewFallbackToolsCache() {
    _value.value = false
    _loading.value = false
    _error.value = null
    _inflight = null
    _resolved = false
}

export function usePreviewFallbackTools() {
    _fetchOnce()
    return {
        toolsEnabled: _value,
        loading: _loading,
        error: _error,
    }
}

export default usePreviewFallbackTools
